import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RewardRow = ({ name, label, current, required, unlocked }) => {
  const pct = Math.min(100, Math.round((current / required) * 100));
  return (
    <div data-testid={`reward-${name}`} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-white">{label}</div>
          <div className="text-xs text-zinc-400">{required} invites required</div>
        </div>
        <div className={`text-xs uppercase font-bold tracking-wider px-2 py-0.5 rounded ${
          unlocked ? 'bg-amber-400 text-black' : 'bg-zinc-800 text-zinc-400'
        }`}>
          {unlocked ? 'Unlocked' : `${current}/${required}`}
        </div>
      </div>
      <div className="mt-3 h-2 bg-zinc-800 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const ReferralsPage = ({ user }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');

  if (!user) return <Navigate to="/login" replace />;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/referral/info`);
        setInfo(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not load referrals');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyLink = async () => {
    if (!info?.referral_link) return;
    try {
      await navigator.clipboard.writeText(info.referral_link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* ignore */ }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-zinc-400">Loading…</div>;
  if (err || !info) return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
      <h1 className="text-2xl font-bold">Could not load referrals</h1>
      <p className="text-zinc-400 mt-2">{err}</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 data-testid="referrals-title" className="text-3xl sm:text-4xl font-extrabold">
        Refer friends. Unlock rewards.
      </h1>
      <p className="text-zinc-400 mt-2">
        Share your link — every signup that uses your code unlocks a tier.
      </p>

      <div className="mt-8 bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Your referral code</div>
        <div data-testid="referral-code" className="mt-1 text-3xl font-extrabold text-amber-400">{info.referral_code}</div>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input
            readOnly
            value={info.referral_link}
            data-testid="referral-link-input"
            className="flex-1 bg-black border border-zinc-800 text-white text-sm rounded-md px-3 py-2"
          />
          <button
            onClick={copyLink}
            data-testid="referral-copy-btn"
            className="px-4 py-2 bg-amber-400 hover:bg-amber-300 text-black text-sm font-semibold rounded-md"
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        </div>
        <div className="mt-4 text-sm text-zinc-300" data-testid="referral-count">
          You've referred <span className="text-amber-400 font-bold">{info.referral_count}</span> people so far.
        </div>
      </div>

      <h2 className="mt-10 text-xl font-bold">Rewards</h2>
      <div className="mt-4 grid gap-3">
        <RewardRow
          name="early_alerts"
          label="Early access to hot deals (3 invites)"
          current={info.referral_count}
          required={info.progress.early_alerts.required}
          unlocked={info.progress.early_alerts.unlocked}
        />
        <RewardRow
          name="premium_tracking"
          label="Free Premium price tracking (5 invites)"
          current={info.referral_count}
          required={info.progress.premium_tracking.required}
          unlocked={info.progress.premium_tracking.unlocked}
        />
        <RewardRow
          name="monthly_giveaway"
          label="Monthly giveaway entry (10 invites)"
          current={info.referral_count}
          required={info.progress.monthly_giveaway.required}
          unlocked={info.progress.monthly_giveaway.unlocked}
        />
      </div>
    </div>
  );
};

export default ReferralsPage;
